import React, { useMemo, useState } from "react";
import { ModalContainer } from "./ModalContainer";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { ModalHeader } from "./ModalHeader";
import { ModalFooter } from "./ModalFooter";
import { ModalFormBody } from "./ModalFormBody";
import { ModalFormField } from "./ModalFormField";
import { useDispatch } from "react-redux";
import { toastOn } from "../../store/slices/toast";
import { requestAuth } from "../../http/httpRequest";
import MapComponentEdit from "../leafLetViews/MapComponentEdit";
import { ConfirmDialog } from "./ConfirmDialog";

export const ModalForm = ({
  confirmMessage,
  setModal,
  label,
  dataValues,
  urlApi,
  method,
  call,
  buttonLabel,
  disabled,
  lock,
}) => {
  const dispatch = useDispatch();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState({ values: null, actions: null });

  const printConfig = dataValues?.printButton || null;
  const hasPrint = !!printConfig?.show;

  const printAreaId = useMemo(() => {
    if (!hasPrint) return null;
    return `print-area-${printConfig.targetName}`;
  }, [hasPrint, printConfig]);

  const printableFields = useMemo(() => {
    if (!hasPrint) return [];
    return (dataValues?.fields || []).filter((f) => f.printable);
  }, [dataValues?.fields, hasPrint]);

  const normalFields = useMemo(() => {
    if (!hasPrint) return [];
    return (dataValues?.fields || []).filter((f) => !f.printable);
  }, [dataValues?.fields, hasPrint]);

  const handlePrint = () => {
    if (!printAreaId) return;
    const element = document.getElementById(printAreaId);
    if (!element) return;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.top = "-9999px";
    iframe.style.left = "-9999px";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const linkTags = Array.from(
      document.querySelectorAll('link[rel="stylesheet"]')
    )
      .map((link) => `<link rel="stylesheet" href="${link.href}">`)
      .join("\n");

    const styleTags = Array.from(document.querySelectorAll("style"))
      .map((style) => `<style>${style.innerHTML}</style>`)
      .join("\n");

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>${printConfig?.title || "Impresión"}</title>
          ${linkTags}
          ${styleTags}
          <style>
            @page {
              margin: 0;
              size: auto;
            }
            body {
              background: white !important;
              padding: 10mm !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            * {
              max-height: none !important;
              overflow: visible !important;
              overflow-x: visible !important;
              overflow-y: visible !important;
            }
          </style>
        </head>
        <body>
          ${element.innerHTML}
        </body>
      </html>
    `);
    iframeDoc.close();

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        document.body.removeChild(iframe);
      }, 500);
    };
  };

  const doSubmit = (values, actions) => {
    requestAuth(method, urlApi, values)
      .then((response) => {
        setModal(false);
        call?.();
        dispatch(toastOn({ type: "success", message: response.data.message }));
      })
      .catch((error) => {
        dispatch(
          toastOn({
            type: "danger",
            message:
              error?.response?.data?.message ??
              "Error al enviar el formulario!",
          })
        );
      })
      .finally(() => {
        actions.setSubmitting(false);
        setConfirmOpen(false);
        setPending({ values: null, actions: null });
      });
  };

  return (
    <ModalContainer
      setModal={setModal}
      width={
        dataValues.fields != null && dataValues.fields.length > 0
          ? "md:max-w-5xl"
          : "md:max-w-min"
      }
    >
      <Formik
        initialValues={dataValues.initialValues}
        validationSchema={dataValues.fieldsValidation ?? Yup.object().shape({})}
        onSubmit={(values, actions) => {
          if (!confirmMessage) {
            return doSubmit(values, actions);
          }
          setPending({ values, actions });
          setConfirmOpen(true);
        }}
      >
        {({
          errors,
          isSubmitting,
          touched,
          values,
          submitForm,
          handleChange,
          setFieldValue,
        }) => (
          <Form
            className="relative bg-white rounded-lg shadow dark:bg-[#273242]"
            autoComplete="off"
          >
            <ModalHeader setModal={setModal} label={label} />

            {dataValues.fields != null && dataValues.fields.length > 0 && (
              <>
                {!hasPrint && (
                  <ModalFormBody>
                    <ModalFormField
                      fields={dataValues.fields}
                      errors={errors}
                      touched={touched}
                      values={values}
                      setValues={setFieldValue}
                      handleChange={handleChange}
                      calculators={dataValues.calculators}
                      submitForm={submitForm}
                    />
                  </ModalFormBody>
                )}

                {hasPrint && printableFields.length > 0 && (
                  <div id={printAreaId}>
                    <ModalFormBody>
                      <ModalFormField
                        fields={printableFields}
                        errors={errors}
                        touched={touched}
                        values={values}
                        setValues={setFieldValue}
                        handleChange={handleChange}
                        calculators={dataValues.calculators}
                        submitForm={submitForm}
                      />
                    </ModalFormBody>
                  </div>
                )}

                {/* CON print: campos normales fuera del área imprimible */}
                {hasPrint && normalFields.length > 0 && (
                  <div className="px-6 py-3">
                    <ModalFormField
                      fields={normalFields}
                      errors={errors}
                      touched={touched}
                      values={values}
                      setValues={setFieldValue}
                      handleChange={handleChange}
                      calculators={dataValues.calculators}
                      submitForm={submitForm}
                    />
                  </div>
                )}
              </>
            )}

            {values?.hasOwnProperty("geolocalizacion") && (
              <div className="p-3">
                <MapComponentEdit
                  data={{ geolocalizacion: values.geolocalizacion }}
                  onChange={(newGeo) =>
                    setFieldValue("geolocalizacion", newGeo)
                  }
                />
              </div>
            )}

            {!lock && (
              <>
                {hasPrint && printConfig?.show && (
                  <div className="px-6 pb-2 flex justify-start">
                    <button
                      type="button"
                      onClick={handlePrint}
                      className="px-4 py-2 rounded-lg dark:bg-slate-700 bg-slate-600 text-white"
                    >
                      {printConfig.label || "Imprimir"}
                    </button>
                  </div>
                )}

                <ModalFooter
                  buttonAccept={buttonLabel}
                  cancel={setModal}
                  disabled={disabled || values.errorDistribucion}
                  isSubmitting={isSubmitting}
                />
              </>
            )}

            <ConfirmDialog
              open={confirmOpen}
              title="Confirmar registro"
              message={
                confirmMessage ||
                "¿Verificaste que los datos ingresados sean correctos?"
              }
              confirmText="Aceptar"
              cancelText="Cancelar"
              onConfirm={() => {
                if (pending.values && pending.actions) {
                  doSubmit(pending.values, pending.actions);
                } else {
                  setConfirmOpen(false);
                }
              }}
              onCancel={() => {
                if (pending.actions) pending.actions.setSubmitting(false);
                setConfirmOpen(false);
                setPending({ values: null, actions: null });
              }}
            />
          </Form>
        )}
      </Formik>
    </ModalContainer>
  );
};